/**
 * Opening the phone's own SMS app with a number and a message filled in, for the owner to press
 * Send (Home's "Text your team their invite"). The text goes from the owner's phone and SIM: no SMS
 * provider and no DLT registration are involved, and nothing is sent until they tap Send.
 *
 * It is an `sms:` link. In the Android app the WebView does not open it itself: Capacitor's bridge
 * sees a navigation to a scheme that is not the app's and hands it to Android as an ACTION_VIEW
 * intent (Bridge#launchIntent), which opens the SMS app. A phone browser does the same. A desktop
 * browser usually has nothing for `sms:`, so the caller copies the message there instead
 * (`canComposeSms`).
 *
 * The number and the link are never logged.
 */

import { isNativeApp } from './nativeBrowser.js';

/**
 * `sms:+919876543210?body=...`. `?body=` is what Android reads (iOS also accepts it for a single
 * recipient). The body is fully percent-encoded so a `&` or `#` in a link cannot end it early.
 */
export function smsComposerUrl(phone: string, body: string): string {
  const recipient = phone.replace(/[^\d+]/g, '');
  return `sms:${recipient}?body=${encodeURIComponent(body)}`;
}

/** True where an `sms:` link opens an SMS app: the phone app, and phone browsers. */
export function canComposeSms(): boolean {
  if (isNativeApp()) return true;
  if (typeof navigator === 'undefined') return false;
  const data = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (data?.mobile) return true;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * True in a phone BROWSER (not the app): there an `sms:` navigation must happen inside the tap
 * itself — after an `await` the browser may treat it as a navigation nobody asked for and block it.
 * The caller then has what goes in the message ready before the tap. In the app the Capacitor
 * bridge hands any `sms:` navigation to Android, so it can follow a request.
 */
export function smsNeedsTap(): boolean {
  return !isNativeApp() && canComposeSms();
}

/**
 * Open the SMS app on `phone` with `body` filled in. A top-level navigation, not `window.open`:
 * that is what the Capacitor bridge turns into an intent, and a browser treats an external scheme
 * as a hand-off, leaving this page where it is.
 */
export function openSmsComposer(phone: string, body: string): void {
  window.location.href = smsComposerUrl(phone, body);
}
